import { Button } from "@/components/ui/button.tsx";
import { Menu, MenuContent, MenuDescription, MenuItem, MenuLabel } from "@/components/ui/menu.tsx";
import { ProgressCircle } from "@/components/ui/progress-circle.tsx";
import { ArrowDownTrayIcon, ChevronDownIcon, DocumentTextIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { usePrint } from "@packages/core-client/print/usePrint.ts";
import { useI18nextInstance } from "@squide/i18next";
import { useCallback, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AnalyticsExportFormats, type AnalyticsTab, useAnalyticsTracking } from "../hooks/useAnalyticsTracking.ts";
import { i18NextInstanceKey } from "../i18n/i18next.ts";

export interface ExportMenuProps {
    children: ReactNode;
    isBusy?: boolean;
}

export function ExportMenu({ children, isBusy = false }: ExportMenuProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ExportMenu", { i18n: i18nextInstance });
    const { isPreparing } = usePrint();

    const isPending = isPreparing || isBusy;

    return (
        <Menu>
            <Button intent="outline" isPending={isPending} className="[--btn-bg:var(--color-card)] print:hidden">
                {isPending ? <ProgressCircle isIndeterminate aria-label={t("preparing")} /> : <ArrowDownTrayIcon aria-hidden="true" />}
                <span>{isPending ? t("preparing") : t("label")}</span>
                <ChevronDownIcon aria-hidden="true" />
            </Button>
            <MenuContent placement="bottom end">{children}</MenuContent>
        </Menu>
    );
}

export interface ExportPdfMenuItemProps {
    tab: AnalyticsTab;
}

export function ExportPdfMenuItem({ tab }: ExportPdfMenuItemProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ExportMenu", { i18n: i18nextInstance });
    const { print } = usePrint();
    const { trackExported } = useAnalyticsTracking();

    const handleAction = useCallback(() => {
        trackExported(AnalyticsExportFormats.PDF, tab);
        void print();
    }, [print, trackExported, tab]);

    return (
        <MenuItem onAction={handleAction} textValue={t("pdf")}>
            <DocumentTextIcon aria-hidden="true" />
            <MenuLabel>{t("pdf")}</MenuLabel>
        </MenuItem>
    );
}

export interface ExportCsvMenuItemProps {
    onAction: () => void;
    unavailableReason?: string;
    label?: string;
}

export function ExportCsvMenuItem({ onAction, unavailableReason, label }: ExportCsvMenuItemProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("ExportMenu", { i18n: i18nextInstance });
    const text = label ?? t("csv");

    return (
        <MenuItem isDisabled={Boolean(unavailableReason)} onAction={onAction} textValue={text}>
            <TableCellsIcon aria-hidden="true" />
            <MenuLabel>{text}</MenuLabel>
            {unavailableReason ? <MenuDescription>{unavailableReason}</MenuDescription> : null}
        </MenuItem>
    );
}

export interface ExportExcelMenuItemProps {
    onAction: () => void;
    unavailableReason?: string;
    label: string;
}

export function ExportExcelMenuItem({ onAction, unavailableReason, label }: ExportExcelMenuItemProps) {
    return (
        <MenuItem isDisabled={Boolean(unavailableReason)} onAction={onAction} textValue={label}>
            <TableCellsIcon aria-hidden="true" />
            <MenuLabel>{label}</MenuLabel>
            {unavailableReason ? <MenuDescription>{unavailableReason}</MenuDescription> : null}
        </MenuItem>
    );
}
